import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserCog, Plus, Edit, Trash2, Save, X, Shield, Search, Filter, Users, Mail, User, Crown, Star, AlertCircle } from 'lucide-react';
import { usersAPI } from '../services/api';

const USER_ROLES = [
  { value: 'admin', labelKey: 'common.roleAdministrator' },
  { value: 'operator', labelKey: 'common.roleOperation' },
  { value: 'viewer', labelKey: 'common.roleViewer' },
];

const emptyUser = { username: '', email: '', full_name: '', role: 'viewer', password: '', is_active: true };

const ManagementUserProfile = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(20);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersAPI.list({ page, limit: perPage, search });
      const data = res?.data || {};
      setUsers(data.users || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      setError(e?.message || t('managementUserProfile.loadError'));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when page/search change
  }, [page, search]);

  const openCreate = () => { setEditing(null); setForm(emptyUser); setShowForm(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: '' }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(emptyUser); setEditing(null); };

  const save = async () => {
    try {
      if (editing) await usersAPI.update(editing.id, { ...form, password: form.password || undefined });
      else await usersAPI.create(form);
      closeForm();
      load();
    } catch (e) {
      setError(e?.message || t('managementUserProfile.saveError'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('managementUserProfile.confirmDelete'))) return;
    try { await usersAPI.remove(id); load(); } catch (e) { setError(e?.message || t('managementUserProfile.deleteError')); }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / perPage)), [total, perPage]);

  const getRoleLabel = (role) => {
    const found = USER_ROLES.find(r => r.value === (role || '').toLowerCase());
    return found ? t(found.labelKey) : (role || t('common.unknown'));
  };

  const getRoleIcon = (role) => {
    switch((role || '').toLowerCase()) {
      case 'admin': return <Crown className="h-4 w-4 text-red-600" />;
      case 'operator': return <Star className="h-4 w-4 text-blue-600" />;
      case 'viewer': return <User className="h-4 w-4 text-green-600" />;
      default: return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch((role || '').toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'operator': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'viewer': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <UserCog className="h-8 w-8 mr-3 text-blue-600" />
              {t('managementUserProfile.title')}
            </h1>
            <p className="text-gray-600 mt-2">{t('managementUserProfile.subtitle')}</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{users.length}</div>
                <div className="text-sm text-gray-500">{t('managementUserProfile.totalUsers')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{users.filter(u => u.is_active).length}</div>
                <div className="text-sm text-gray-500">{t('common.active')}</div>
              </div>
            </div>
            <button
              onClick={openCreate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>{t('managementUserProfile.addUser')}</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-700 font-medium">{error}</span>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">{t('managementUserProfile.directory')}</h2>
              </div>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input 
                    value={search} 
                    onChange={(e)=>{setPage(1); setSearch(e.target.value);}} 
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                    placeholder={t('managementUserProfile.searchPlaceholder')}
                  />
                </div>
                <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Filter className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="text-gray-500 mt-2">{t('managementUserProfile.loading')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementUserProfile.colUser')}</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('managementUserProfile.colContact')}</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.role')}</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 flex items-center justify-center text-white font-medium">
                            {u.username.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{u.username}</div>
                            <div className="text-sm text-gray-500">{u.full_name || t('managementUserProfile.noFullName')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Mail className="h-4 w-4 text-gray-400 mr-2" />
                          {u.email || t('managementUserProfile.noEmail')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(u.role)}`}>
                          {getRoleIcon(u.role)}
                          <span className="ml-1">{getRoleLabel(u.role)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {u.is_active ? (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                            {t('common.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
                            {t('common.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={()=>openEdit(u)} 
                            className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <Edit className="h-4 w-4"/>
                          </button>
                          <button 
                            onClick={()=>remove(u.id)} 
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4"/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length===0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">{t('managementUserProfile.noUsers')}</p>
                  <p className="text-gray-400 text-sm">{t('managementUserProfile.tryAdjust')}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t('managementUserProfile.showing', { from: ((page - 1) * perPage) + 1, to: Math.min(page * perPage, total), total })}
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  disabled={page===1} 
                  onClick={()=>setPage(p=>Math.max(1,p-1))} 
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.previous')}
                </button>
                <span className="text-sm text-gray-600">{t('common.pageOf', { page, total: totalPages })}</span>
                <button
                  disabled={page===totalPages}
                  onClick={()=>setPage(p=>Math.min(totalPages,p+1))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg mr-3">
                      <Shield className="h-5 w-5 text-white"/>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{editing ? t('managementUserProfile.editUser') : t('managementUserProfile.addNewUser')}</h3>
                      <p className="text-gray-500 text-sm">{editing ? t('managementUserProfile.updateInfo') : t('managementUserProfile.createAccount')}</p>
                    </div>
                  </div>
                  <button 
                    onClick={closeForm} 
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5"/>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.usernameLabel')}</label>
                    <div className="relative">
                      <User className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input 
                        value={form.username} 
                        onChange={e=>setForm({...form, username:e.target.value})} 
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                        placeholder={t('managementUserProfile.enterUsername')}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.fullName')}</label>
                    <input
                      value={form.full_name}
                      onChange={e=>setForm({...form, full_name:e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder={t('managementUserProfile.enterFullName')}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.emailAddress')}</label>
                    <div className="relative">
                      <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input 
                        type="email" 
                        value={form.email} 
                        onChange={e=>setForm({...form, email:e.target.value})} 
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" 
                        placeholder={t('managementUserProfile.enterEmail')}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('managementUserProfile.userRole')}</label>
                    <select
                      value={form.role}
                      onChange={e=>setForm({...form, role:e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      {USER_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('managementUserProfile.password')} {editing && <span className="text-gray-400">{t('managementUserProfile.passwordKeepBlank')}</span>}
                    </label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={e=>setForm({...form, password:e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder={editing ? t('managementUserProfile.enterNewPassword') : t('managementUserProfile.enterPassword')}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{t('managementUserProfile.accountStatus')}</div>
                        <div className="text-sm text-gray-500">{t('managementUserProfile.accountStatusHint')}</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={form.is_active} 
                          onChange={e=>setForm({...form, is_active:e.target.checked})} 
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
                <button 
                  onClick={closeForm} 
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button 
                  onClick={save} 
                  className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all flex items-center space-x-2"
                >
                  <Save className="h-4 w-4"/>
                  <span>{editing ? t('managementUserProfile.updateUser') : t('managementUserProfile.createUser')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ManagementUserProfile;
